import {
  ColDef,
  GridReadyEvent,
  IServerSideDatasource,
  RowDoubleClickedEvent,
} from "ag-grid-community";
import { useNavigate } from "react-router-dom";
import AgGrid from "../../../UI/AgGrid";
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
} from "react";
import { createGraphQLClientWithMiddleware } from "../../../../services/graphqlClient";
import { dateFilterModel } from "../../../../utils/Formatter/AgGridFilter";
import { AgGridReact } from "ag-grid-react";
import { GraphQLClient } from "graphql-request";
import { GOODS_RETURN_VIEW } from "../../../../services/AgGrid/InventoryAggrid";
import { IGoodsReturn } from "../../../../types/Inventory/GoodsReturn";
import { useGoodsReturnColumnDefs } from "./columnDef";
import { branchInfoFromSessionStorage } from "../../../../utils/Global";

interface Props {
  gridRef: RefObject<AgGridReact<IGoodsReturn>>;
  isFilter?: string | null;
  setRowCount?: Dispatch<SetStateAction<number>>;
  referenceUniqueId?: string;
}

const GoodsReturnTable = ({
  gridRef,
  isFilter,
  setRowCount,
  referenceUniqueId,
}: Props) => {
  const navigate = useNavigate();

  const columnDef: ColDef[] = useGoodsReturnColumnDefs(referenceUniqueId);

  const graphQLClientWithHeaderItem: GraphQLClient =
    createGraphQLClientWithMiddleware("wms");

  const datasource: IServerSideDatasource = {
    async getRows(params) {
      const { request } = params;
      const { startRow, endRow, filterModel, sortModel } = request;

      const { created_date, posted_date } = filterModel;

      const formatFilter = {
        ...filterModel,
        created_date: dateFilterModel(created_date),
        posted_date: dateFilterModel(posted_date),
      };

      try {
        const { GoodsReturnsView } = await graphQLClientWithHeaderItem.request(
          GOODS_RETURN_VIEW,
          {
            aggridInput: {
              startRow,
              endRow,
              filterModel: formatFilter,
              sortModel,
            },
          }
        );
        if (setRowCount) {
          setRowCount(GoodsReturnsView.count as number);
        }
        params.success({
          rowData: GoodsReturnsView.results as any[],
          rowCount: GoodsReturnsView.count as number,
        });
      } catch (err) {
        params.fail();
      }
    },
  };

  const onFilterChanged = useCallback(
    (params: any) => {
      const instance = params.api.getFilterInstance("aggrid_status");
      const reference = params.api.getFilterInstance("reference_unique_id");
      switch (isFilter) {
        case "draft":
          instance?.setModel({ values: ["draft"] });
          break;
        case "wait_receive":
          instance?.setModel({ values: ["wait_receive"] });
          break;
        case "in_transit":
          instance?.setModel({ values: ["in_transit"] });
          break;
        case "finished":
          instance?.setModel({ values: ["finished"] });
          break;
        case "cancelled":
          instance?.setModel({ values: ["cancelled"] });
          break;
        default:
          instance?.setModel({});
          break;
      }
      reference?.setModel({
        filterType: "text",
        type: "contains",
        filter: referenceUniqueId,
      });
      params.api.onFilterChanged();
    },
    [isFilter, referenceUniqueId]
  );

  const onGridReady = (params: GridReadyEvent) => {
    const { branch_id, is_hq } = branchInfoFromSessionStorage();

    const instanceฺฺBranchId = params.api.getFilterInstance("branch_id");
    instanceฺฺBranchId?.setModel({
      filterType: "number",
      type: "equals",
      filter: is_hq === "false" && branch_id ? parseInt(branch_id) : undefined,
    });
    onFilterChanged(params);
    params.api.setServerSideDatasource(datasource);
    onFilterChanged(params);
    params.api.setServerSideDatasource(datasource);
  };

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      onFilterChanged(gridRef.current);
    }
  }, [gridRef, onFilterChanged]);

  const onRowDoubleClicked = (params: RowDoubleClickedEvent) => {
    navigate(`/inventory/goods-return/${encodeURIComponent(params.data.id)}`);
  };

  return (
    <AgGrid
      ref={gridRef}
      columnDefs={columnDef}
      height={665}
      onRowDoubleClicked={onRowDoubleClicked}
      onGridReady={onGridReady}
      path={"/inventory/goods-return"}
    />
  );
};

export default GoodsReturnTable;
